import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Link } from '@tiptap/extension-link';
import { Image } from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Placeholder } from '@tiptap/extension-placeholder';
import { forwardRef, useEffect, useCallback, useState } from 'react';
import { ResizableImage } from './ResizableImage';
import {
  Box,
  Group,
  ActionIcon,
  Divider,
  Tooltip,
  ColorInput,
  TextInput as MantineTextInput,
  Modal,
  Stack,
  useMantineTheme,
  useMantineColorScheme,
  Paper,
  Button,
  Select,
  ColorPicker,
  Popover,
  Tabs,
  FileButton,
  Image as MantineImage,
  Text as MantineText,
  Loader,
  NumberInput,
  Checkbox,
} from '@mantine/core';
import {
  IconBold,
  IconItalic,
  IconUnderline,
  IconStrikethrough,
  IconCode,
  IconH1,
  IconH2,
  IconH3,
  IconList,
  IconListNumbers,
  IconBlockquote,
  IconAlignLeft,
  IconAlignCenter,
  IconAlignRight,
  IconAlignJustified,
  IconArrowBackUp,
  IconArrowForwardUp,
  IconLink,
  IconUnlink,
  IconPhoto,
  IconTable,
  IconClearFormatting,
  IconCode as IconCodeBlock,
  IconPalette,
  IconUpload,
} from '@tabler/icons-react';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';
import { defaultColorScheme } from '../../../theme/theme';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface HtmlEditorProps {
  value?: string;
  onChange?: (html: string, text: string) => void;
  onHtmlChange?: (html: string) => void;
  onTextChange?: (text: string) => void;
  placeholder?: string;
  label?: string;
  description?: string;
  error?: string;
  disabled?: boolean;
  readOnly?: boolean;
  minHeight?: number | string;
  maxHeight?: number | string;
  gradientVariant?: GradientVariant;
  useColorScheme?: boolean;
  showToolbar?: boolean;
  toolbarFeatures?: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    strike?: boolean;
    code?: boolean;
    headings?: boolean;
    lists?: boolean;
    blockquote?: boolean;
    codeBlock?: boolean;
    alignment?: boolean;
    textColor?: boolean;
    fontSize?: boolean;
    link?: boolean;
    image?: boolean;
    table?: boolean;
    clearFormatting?: boolean;
    undo?: boolean;
  };
  customStyles?: React.CSSProperties;
  contentStyles?: string; // CSS string to inject into editor content
  maxImageSize?: number; // Maximum image size in bytes (default: 2MB)
  imageQuality?: number; // JPEG quality 0-1 (default: 0.8)
  maxImageWidth?: number; // Maximum image width in pixels (default: 1200)
  maxImageHeight?: number; // Maximum image height in pixels (default: 1200)
}

const MenuButton = ({
  active,
  disabled,
  onClick,
  children,
  title,
  color,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title: string;
  color?: string;
}) => (
  <Tooltip label={title} position="top" withArrow>
    <ActionIcon
      variant={active ? 'filled' : 'subtle'}
      color={active ? color : 'gray'}
      onClick={onClick}
      disabled={disabled}
      size="md"
    >
      {children}
    </ActionIcon>
  </Tooltip>
);

const HtmlEditor = forwardRef<HTMLDivElement, HtmlEditorProps>(
  (
    {
      value = '',
      onChange,
      onHtmlChange,
      onTextChange,
      placeholder = 'Start typing...',
      label,
      description,
      error,
      disabled = false,
      readOnly = false,
      minHeight = 200,
      maxHeight,
      gradientVariant = 'primary',
      useColorScheme = true,
      showToolbar = true,
      toolbarFeatures = {
        bold: true,
        italic: true,
        underline: true,
        strike: true,
        code: true,
        headings: true,
        lists: true,
        blockquote: true,
        codeBlock: true,
        alignment: true,
        textColor: true,
        fontSize: true,
        link: true,
        image: true,
        table: true,
        clearFormatting: true,
        undo: true,
      },
      customStyles,
      contentStyles,
      maxImageSize = 2 * 1024 * 1024, // 2MB default
      imageQuality = 0.8,
      maxImageWidth = 1200,
      maxImageHeight = 1200,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [textColor, setTextColor] = useState('#000000');
    const [colorPickerOpen, setColorPickerOpen] = useState(false);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string>('');
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imageSize, setImageSize] = useState<{ original: number; compressed: number; width: number; height: number }>({
      original: 0,
      compressed: 0,
      width: 0,
      height: 0,
    });
    const [insertImageWidth, setInsertImageWidth] = useState<number | ''>(''); // Width to insert (percentage or pixels)
    const [compressImage, setCompressImage] = useState(true); // Whether to compress/resize the image
    const [customQuality, setCustomQuality] = useState<number>(imageQuality * 100); // Custom quality percentage
    const [processingImage, setProcessingImage] = useState(false); // Processing state for re-compression

    // Determine which color to use
    let color = theme.other.gradients[gradientVariant].from;

    if (useColorScheme) {
      const effectiveColorSchemeKey = colorSchemeKey || defaultColorScheme.key;
      const colorScheme = theme.other.colorSchemes?.find((cs: any) => cs.key === effectiveColorSchemeKey);
      if (colorScheme) {
        color = colorScheme.primary.from;
      }
    }

    const extensions = [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        paragraph: {
          HTMLAttributes: {
            style: 'margin: 0; line-height: 1.5;',
          },
        },
        hardBreak: {
          keepMarks: true,
        },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TextStyle,
      Color,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      ResizableImage,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
      Placeholder.configure({
        placeholder,
      }),
    ];

    // Prevent duplicate extension registration (React StrictMode/HMR can double-initialize)
    const uniqueExtensions = extensions.filter((ext, index, arr) => {
      const name = (ext as any)?.name;
      if (!name) return true;
      return index === arr.findIndex((item) => (item as any)?.name === name);
    });

    const editor = useEditor({
      extensions: uniqueExtensions,
      content: value,
      editable: !disabled && !readOnly,
      editorProps: {
        handleKeyDown: (view, event) => {
          // Single Enter creates a line break, Shift+Enter or double Enter creates paragraph
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            editor?.commands.setHardBreak();
            return true;
          }
          return false;
        },
      },
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        const text = editor.getText();
        onChange?.(html, text);
        onHtmlChange?.(html);
        onTextChange?.(text);
      },
    });

    // Update editor content when value prop changes
    useEffect(() => {
      if (editor && value !== editor.getHTML()) {
        editor.commands.setContent(value);
      }
    }, [value, editor]);

    // Update editable state
    useEffect(() => {
      if (editor) {
        editor.setEditable(!disabled && !readOnly);
      }
    }, [disabled, readOnly, editor]);

    const setLink = useCallback(() => {
      if (!editor) return;

      const previousUrl = editor.getAttributes('link').href;
      setLinkUrl(previousUrl || '');
      setLinkModalOpen(true);
    }, [editor]);

    const applyLink = useCallback(() => {
      if (!editor) return;

      if (linkUrl === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
      } else {
        editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
      }
      setLinkModalOpen(false);
      setLinkUrl('');
    }, [editor, linkUrl]);

    // Function to process/reprocess image
    const processImage = useCallback(
      async (file: File, quality: number, shouldCompress: boolean) => {
        setProcessingImage(true);

        try {
          // Read the image file
          const reader = new FileReader();
          reader.onload = async (e) => {
            const dataUrl = e.target?.result as string;

            // Create an image element to get dimensions
            const img = new window.Image();
            img.onload = async () => {
              let width = img.width;
              let height = img.height;
              let base64 = dataUrl;
              let finalSize = file.size;

              // Only apply compression/resizing if shouldCompress is enabled
              if (shouldCompress) {
                // Calculate new dimensions if needed
                if (width > maxImageWidth || height > maxImageHeight) {
                  const widthRatio = maxImageWidth / width;
                  const heightRatio = maxImageHeight / height;
                  const ratio = Math.min(widthRatio, heightRatio);
                  width = Math.floor(width * ratio);
                  height = Math.floor(height * ratio);
                }

                // Create canvas to resize and compress
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');

                if (ctx) {
                  ctx.drawImage(img, 0, 0, width, height);

                  // Convert to base64 with quality compression (use quality parameter)
                  const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
                  const qualityToUse = quality / 100;
                  base64 = canvas.toDataURL(mimeType, qualityToUse);

                  // Check final size
                  finalSize = Math.ceil((base64.length * 3) / 4);
                  if (finalSize > maxImageSize) {
                    const sizeMB = (maxImageSize / (1024 * 1024)).toFixed(2);
                    alert(
                      `Compressed image is still too large (${(finalSize / (1024 * 1024)).toFixed(2)}MB). Please reduce quality or use a smaller image (current limit: ${sizeMB}MB)`
                    );
                    setProcessingImage(false);
                    setUploadingImage(false);
                    return;
                  }
                }
              } else {
                // No compression - check if original image exceeds maxImageSize
                const originalSizeInBase64 = Math.ceil((dataUrl.length * 3) / 4);
                if (originalSizeInBase64 > maxImageSize) {
                  const sizeMB = (maxImageSize / (1024 * 1024)).toFixed(2);
                  alert(
                    `Image is too large (${(originalSizeInBase64 / (1024 * 1024)).toFixed(2)}MB). Please enable compression or use a smaller image (current limit: ${sizeMB}MB)`
                  );
                  setProcessingImage(false);
                  setUploadingImage(false);
                  return;
                }
                finalSize = originalSizeInBase64;
              }

              // Store size information
              setImageSize({
                original: file.size,
                compressed: finalSize,
                width,
                height,
              });

              // Set default insert width to 100% (responsive)
              if (!insertImageWidth) {
                setInsertImageWidth(100);
              }

              setImagePreview(base64);
              setProcessingImage(false);
              setUploadingImage(false);
            };
            img.src = dataUrl;
          };
          reader.readAsDataURL(file);
        } catch (error) {
          console.error('Error processing image:', error);
          alert('Failed to process image');
          setProcessingImage(false);
          setUploadingImage(false);
        }
      },
      [maxImageSize, maxImageWidth, maxImageHeight, insertImageWidth]
    );

    const handleImageFileChange = useCallback(
      async (file: File | null) => {
        if (!file) return;
    
        // Validate file type
        if (!file.type.startsWith('image/')) {
          alert('Please select an image file');
          return;
        }
    
        // Validate file size
        if (file.size > maxImageSize) {
          const sizeMB = (maxImageSize / (1024 * 1024)).toFixed(2);
          alert(`Image size must be less than ${sizeMB}MB`);
          return;
        }
    
        setImageFile(file);
        setUploadingImage(true);
    
        // Process the image using the new processImage function
        await processImage(file, customQuality, compressImage);
      },
      [processImage, customQuality, compressImage, maxImageSize]
    );

    const addImage = useCallback(() => {
      if (!editor) return;

      // Use base64 preview if available, otherwise use URL
      const src = imagePreview || imageUrl;
      if (!src) return;

      // Calculate width based on insertImageWidth
      let width = null;
      if (imagePreview && insertImageWidth) {
        if (insertImageWidth <= 100) {
          // Calculate pixel width from percentage
          width = Math.round((imageSize.width * insertImageWidth) / 100);
        } else {
          // Use pixel width directly
          width = insertImageWidth;
        }
      }

      // Insert resizable image
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'resizableImage',
          attrs: {
            src,
            alt: imageFile?.name || '',
            width,
          },
        })
        .run();

      setImageModalOpen(false);
      setImageUrl('');
      setImageFile(null);
      setImagePreview('');
      setInsertImageWidth('');
      setImageSize({ original: 0, compressed: 0, width: 0, height: 0 });
      setCompressImage(true);
      setCustomQuality(imageQuality * 100);
    }, [editor, imageUrl, imagePreview, insertImageWidth, imageFile, imageSize, imageQuality]);

    const addTable = useCallback(() => {
      if (!editor) return;
      editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    }, [editor]);

    if (!editor) {
      return null;
    }

    return (
      <Box ref={ref} style={customStyles}>
        {label && (
          <Box component="label" mb={4} style={{ fontSize: '14px', fontWeight: 500 }}>
            {label}
          </Box>
        )}
        {description && (
          <Box
            mb="xs"
            style={{
              fontSize: '12px',
              color: colorScheme === 'dark' ? theme.colors.gray[4] : theme.colors.gray[6],
            }}
          >
            {description}
          </Box>
        )}

        <Paper
          withBorder
          style={{
            borderColor: error ? theme.colors.red[6] : undefined,
            opacity: disabled ? 0.6 : 1,
          }}
        >
          {/* Toolbar */}
          {showToolbar && !readOnly && (
            <Box
              p="xs"
              style={{
                borderBottom: `1px solid ${colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[3]}`,
                backgroundColor: colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
              }}
            >
              <Group gap={4} wrap="wrap">
                {/* Undo/Redo */}
                {toolbarFeatures.undo && (
                  <>
                    <MenuButton
                      title="Undo (Ctrl+Z)"
                      onClick={() => editor.chain().focus().undo().run()}
                      disabled={!editor.can().undo()}
                    >
                      <IconArrowBackUp size={18} />
                    </MenuButton>
                    <MenuButton
                      title="Redo (Ctrl+Y)"
                      onClick={() => editor.chain().focus().redo().run()}
                      disabled={!editor.can().redo()}
                    >
                      <IconArrowForwardUp size={18} />
                    </MenuButton>
                    <Divider orientation="vertical" />
                  </>
                )}

                {/* Text formatting */}
                {toolbarFeatures.bold && (
                  <MenuButton
                    title="Bold (Ctrl+B)"
                    active={editor.isActive('bold')}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    color={color}
                  >
                    <IconBold size={18} />
                  </MenuButton>
                )}
                {toolbarFeatures.italic && (
                  <MenuButton
                    title="Italic (Ctrl+I)"
                    active={editor.isActive('italic')}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    color={color}
                  >
                    <IconItalic size={18} />
                  </MenuButton>
                )}
                {toolbarFeatures.underline && (
                  <MenuButton
                    title="Underline (Ctrl+U)"
                    active={editor.isActive('underline')}
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    color={color}
                  >
                    <IconUnderline size={18} />
                  </MenuButton>
                )}
                {toolbarFeatures.strike && (
                  <MenuButton
                    title="Strikethrough (Ctrl+Shift+X)"
                    active={editor.isActive('strike')}
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    color={color}
                  >
                    <IconStrikethrough size={18} />
                  </MenuButton>
                )}
                {toolbarFeatures.code && (
                  <MenuButton
                    title="Code (Ctrl+E)"
                    active={editor.isActive('code')}
                    onClick={() => editor.chain().focus().toggleCode().run()}
                    color={color}
                  >
                    <IconCode size={18} />
                  </MenuButton>
                )}

                {(toolbarFeatures.bold ||
                  toolbarFeatures.italic ||
                  toolbarFeatures.underline ||
                  toolbarFeatures.strike ||
                  toolbarFeatures.code) && <Divider orientation="vertical" />}

                {/* Headings */}
                {toolbarFeatures.headings && (
                  <>
                    <MenuButton
                      title="Heading 1 (Ctrl+Alt+1)"
                      active={editor.isActive('heading', { level: 1 })}
                      onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                      color={color}
                    >
                      <IconH1 size={18} />
                    </MenuButton>
                    <MenuButton
                      title="Heading 2 (Ctrl+Alt+2)"
                      active={editor.isActive('heading', { level: 2 })}
                      onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                      color={color}
                    >
                      <IconH2 size={18} />
                    </MenuButton>
                    <MenuButton
                      title="Heading 3 (Ctrl+Alt+3)"
                      active={editor.isActive('heading', { level: 3 })}
                      onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                      color={color}
                    >
                      <IconH3 size={18} />
                    </MenuButton>
                    <Divider orientation="vertical" />
                  </>
                )}

                {/* Lists */}
                {toolbarFeatures.lists && (
                  <>
                    <MenuButton
                      title="Bullet List (Ctrl+Shift+8)"
                      active={editor.isActive('bulletList')}
                      onClick={() => editor.chain().focus().toggleBulletList().run()}
                      color={color}
                    >
                      <IconList size={18} />
                    </MenuButton>
                    <MenuButton
                      title="Numbered List (Ctrl+Shift+7)"
                      active={editor.isActive('orderedList')}
                      onClick={() => editor.chain().focus().toggleOrderedList().run()}
                      color={color}
                    >
                      <IconListNumbers size={18} />
                    </MenuButton>
                    <Divider orientation="vertical" />
                  </>
                )}

                {/* Blockquote & Code Block */}
                {toolbarFeatures.blockquote && (
                  <MenuButton
                    title="Blockquote (Ctrl+Shift+B)"
                    active={editor.isActive('blockquote')}
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    color={color}
                  >
                    <IconBlockquote size={18} />
                  </MenuButton>
                )}
                {toolbarFeatures.codeBlock && (
                  <MenuButton
                    title="Code Block (Ctrl+Alt+C)"
                    active={editor.isActive('codeBlock')}
                    onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                    color={color}
                  >
                    <IconCodeBlock size={18} />
                  </MenuButton>
                )}

                {(toolbarFeatures.blockquote || toolbarFeatures.codeBlock) && (
                  <Divider orientation="vertical" />
                )}

                {/* Text Alignment */}
                {toolbarFeatures.alignment && (
                  <>
                    <MenuButton
                      title="Align Left (Ctrl+Shift+L)"
                      active={editor.isActive({ textAlign: 'left' })}
                      onClick={() => editor.chain().focus().setTextAlign('left').run()}
                      color={color}
                    >
                      <IconAlignLeft size={18} />
                    </MenuButton>
                    <MenuButton
                      title="Align Center (Ctrl+Shift+E)"
                      active={editor.isActive({ textAlign: 'center' })}
                      onClick={() => editor.chain().focus().setTextAlign('center').run()}
                      color={color}
                    >
                      <IconAlignCenter size={18} />
                    </MenuButton>
                    <MenuButton
                      title="Align Right (Ctrl+Shift+R)"
                      active={editor.isActive({ textAlign: 'right' })}
                      onClick={() => editor.chain().focus().setTextAlign('right').run()}
                      color={color}
                    >
                      <IconAlignRight size={18} />
                    </MenuButton>
                    <MenuButton
                      title="Justify (Ctrl+Shift+J)"
                      active={editor.isActive({ textAlign: 'justify' })}
                      onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                      color={color}
                    >
                      <IconAlignJustified size={18} />
                    </MenuButton>
                    <Divider orientation="vertical" />
                  </>
                )}

                {/* Text Color */}
                {toolbarFeatures.textColor && (
                  <>
                    <Popover opened={colorPickerOpen} onChange={setColorPickerOpen}>
                      <Popover.Target>
                        <Tooltip label="Text Color" position="top" withArrow>
                          <ActionIcon
                            variant="subtle"
                            onClick={() => setColorPickerOpen(!colorPickerOpen)}
                            size="md"
                          >
                            <IconPalette size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Popover.Target>
                      <Popover.Dropdown>
                        <Stack gap="xs">
                          <ColorPicker
                            value={textColor}
                            onChange={(newColor) => {
                              setTextColor(newColor);
                              editor.chain().focus().setColor(newColor).run();
                            }}
                            format="hex"
                            swatches={[
                              '#000000',
                              '#868e96',
                              '#fa5252',
                              '#e64980',
                              '#be4bdb',
                              '#7950f2',
                              '#4c6ef5',
                              '#228be6',
                              '#15aabf',
                              '#12b886',
                              '#40c057',
                              '#82c91e',
                              '#fab005',
                              '#fd7e14',
                            ]}
                          />
                        </Stack>
                      </Popover.Dropdown>
                    </Popover>
                    <Divider orientation="vertical" />
                  </>
                )}

                {/* Font Size */}
                {toolbarFeatures.fontSize && (
                  <>
                    <Select
                      data={[
                        { value: 'small', label: 'Small' },
                        { value: 'normal', label: 'Normal' },
                        { value: 'large', label: 'Large' },
                        { value: 'huge', label: 'Huge' },
                      ]}
                      placeholder="Size"
                      size="xs"
                      w={100}
                      onChange={(value) => {
                        if (!value) return;
                        const sizes = {
                          small: '0.875em',
                          normal: '1em',
                          large: '1.25em',
                          huge: '1.5em',
                        };
                        const fontSize = sizes[value as keyof typeof sizes];
                        editor.chain().focus().setMark('textStyle', { fontSize }).run();
                      }}
                    />
                    <Divider orientation="vertical" />
                  </>
                )}

                {/* Link */}
                {toolbarFeatures.link && (
                  <>
                    <MenuButton
                      title="Add Link (Ctrl+K)"
                      active={editor.isActive('link')}
                      onClick={setLink}
                      color={color}
                    >
                      <IconLink size={18} />
                    </MenuButton>
                    <MenuButton
                      title="Remove Link"
                      onClick={() => editor.chain().focus().unsetLink().run()}
                      disabled={!editor.isActive('link')}
                    >
                      <IconUnlink size={18} />
                    </MenuButton>
                    <Divider orientation="vertical" />
                  </>
                )}

                {/* Image */}
                {toolbarFeatures.image && (
                  <>
                    <MenuButton title="Add Image" onClick={() => setImageModalOpen(true)} color={color}>
                      <IconPhoto size={18} />
                    </MenuButton>
                    <Divider orientation="vertical" />
                  </>
                )}

                {/* Table */}
                {toolbarFeatures.table && (
                  <>
                    <MenuButton title="Insert Table" onClick={addTable} color={color}>
                      <IconTable size={18} />
                    </MenuButton>
                    <Divider orientation="vertical" />
                  </>
                )}

                {/* Clear Formatting */}
                {toolbarFeatures.clearFormatting && (
                  <MenuButton
                    title="Clear Formatting"
                    onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                  >
                    <IconClearFormatting size={18} />
                  </MenuButton>
                )}
              </Group>
            </Box>
          )}

          {/* Editor Content */}
          <Box
            p="md"
            style={{
              minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight,
              maxHeight: maxHeight ? (typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight) : undefined,
              overflow: 'auto',
            }}
          >
            <style>
              {`
                .ProseMirror {
                  outline: none;
                  min-height: ${typeof minHeight === 'number' ? minHeight - 32 : '168'}px;
                  line-height: 1.5;
                }

                .ProseMirror p {
                  margin: 0;
                  line-height: 1.5;
                }

                .ProseMirror br {
                  content: '';
                  display: block;
                  margin: 0;
                }

                .ProseMirror p.is-editor-empty:first-child::before {
                  color: ${theme.colors.gray[5]};
                  content: attr(data-placeholder);
                  float: left;
                  height: 0;
                  pointer-events: none;
                }

                /* WCAG compliant heading sizes */
                .ProseMirror h1 {
                  font-size: 2.625em;
                  font-weight: bold;
                  margin: 0.5em 0 0.25em 0;
                  line-height: 1.2;
                }

                .ProseMirror h2 {
                  font-size: 1.75em;
                  font-weight: bold;
                  margin: 0.5em 0 0.25em 0;
                  line-height: 1.25;
                }

                .ProseMirror h3 {
                  font-size: 1.375em;
                  font-weight: bold;
                  margin: 0.5em 0 0.25em 0;
                  line-height: 1.3;
                }

                .ProseMirror ul,
                .ProseMirror ol {
                  padding-left: 2em;
                  margin: 1em 0;
                }

                .ProseMirror ul {
                  list-style-type: disc;
                }

                .ProseMirror ol {
                  list-style-type: decimal;
                }

                .ProseMirror blockquote {
                  border-left: 3px solid ${color};
                  padding-left: 1em;
                  margin: 1em 0;
                  font-style: italic;
                }

                .ProseMirror code {
                  background-color: ${theme.colors.gray[1]};
                  padding: 0.2em 0.4em;
                  border-radius: 3px;
                  font-family: 'Courier New', Courier, monospace;
                  font-size: 0.9em;
                }

                .ProseMirror pre {
                  background-color: ${theme.colors.gray[1]};
                  padding: 1em;
                  border-radius: 5px;
                  overflow-x: auto;
                  margin: 1em 0;
                }

                .ProseMirror pre code {
                  background: none;
                  padding: 0;
                  font-size: 0.875em;
                  color: inherit;
                }

                .ProseMirror a {
                  color: ${color};
                  text-decoration: underline;
                  cursor: pointer;
                }

                .ProseMirror a:hover {
                  opacity: 0.8;
                }

                .ProseMirror table {
                  border-collapse: collapse;
                  table-layout: fixed;
                  width: 100%;
                  margin: 1em 0;
                  overflow: hidden;
                }

                .ProseMirror table td,
                .ProseMirror table th {
                  border: 1px solid ${theme.colors.gray[4]};
                  padding: 0.5em;
                  vertical-align: top;
                  box-sizing: border-box;
                  position: relative;
                }

                .ProseMirror table th {
                  font-weight: bold;
                  text-align: left;
                  background-color: ${theme.colors.gray[1]};
                }

                .ProseMirror img {
                  max-width: 100%;
                  height: auto;
                  display: inline-block;
                  margin: 0.5em 0;
                  cursor: pointer;
                  border: 2px solid transparent;
                  border-radius: 4px;
                  transition: all 0.2s ease;
                  resize: both;
                  overflow: auto;
                }

                .ProseMirror img:hover {
                  border-color: ${color};
                  box-shadow: 0 0 0 3px ${color}22;
                }

                .ProseMirror img.ProseMirror-selectednode {
                  border-color: ${color};
                  box-shadow: 0 0 0 3px ${color}44;
                  outline: none;
                }

                ${contentStyles || ''}
              `}
            </style>
            <EditorContent editor={editor} />
          </Box>
        </Paper>

        {error && (
          <Box mt={4} style={{ fontSize: '12px', color: theme.colors.red[6] }}>
            {error}
          </Box>
        )}

        {/* Link Modal */}
        <Modal
          opened={linkModalOpen}
          onClose={() => {
            setLinkModalOpen(false);
            setLinkUrl('');
          }}
          title="Add Link"
          size="md"
        >
          <Stack>
            <MantineTextInput
              label="URL"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  applyLink();
                }
              }}
              autoFocus
            />
            <Group justify="flex-end" gap="sm">
              <Button
                variant="subtle"
                onClick={() => {
                  setLinkModalOpen(false);
                  setLinkUrl('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="filled"
                style={{ backgroundColor: color }}
                onClick={applyLink}
              >
                Apply Link
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Image Modal */}
        <Modal
          opened={imageModalOpen}
          onClose={() => {
            setImageModalOpen(false);
            setImageUrl('');
            setImageFile(null);
            setImagePreview('');
            setInsertImageWidth('');
            setImageSize({ original: 0, compressed: 0, width: 0, height: 0 });
            setCompressImage(true);
            setCustomQuality(imageQuality * 100);
          }}
          title="Add Image"
          size="md"
        >
          <Tabs defaultValue="upload">
            <Tabs.List>
              <Tabs.Tab value="upload" leftSection={<IconUpload size={14} />}>
                Upload Image
              </Tabs.Tab>
              <Tabs.Tab value="url" leftSection={<IconLink size={14} />}>
                Image URL
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="upload" pt="md">
              <Stack gap="md">
                <Box>
                  <MantineText size="sm" fw={500} mb="xs" style={accessibilityStyles}>
                    Select an image file
                  </MantineText>
                  <MantineText size="xs" c="dimmed" mb="sm" style={accessibilityStyles}>
                    Maximum size: {(maxImageSize / (1024 * 1024)).toFixed(2)}MB, Max dimensions:{' '}
                    {maxImageWidth}x{maxImageHeight}px, Quality: {(imageQuality * 100).toFixed(0)}%
                  </MantineText>
                  <FileButton onChange={handleImageFileChange} accept="image/*">
                    {(props) => (
                      <Button {...props} variant="light" leftSection={<IconUpload size={16} />}>
                        Choose Image
                      </Button>
                    )}
                  </FileButton>
                </Box>

                <Checkbox
                  checked={compressImage}
                  onChange={(event) => {
                    const newCompressionState = event.currentTarget.checked;
                    setCompressImage(newCompressionState);
                    // Reset quality to default when toggling
                    setCustomQuality(imageQuality * 100);
                    // Reprocess image if one is loaded
                    if (imageFile) {
                      processImage(imageFile, imageQuality * 100, newCompressionState);
                    }
                  }}
                  label={
                    <Box>
                      <MantineText size="sm" fw={500} style={accessibilityStyles}>
                        Compress image to fit designed size
                      </MantineText>
                      <MantineText size="xs" c="dimmed" style={accessibilityStyles}>
                        When enabled, images will be resized to max {maxImageWidth}x{maxImageHeight}px and compressed.
                        When disabled, original image dimensions and quality are preserved.
                      </MantineText>
                    </Box>
                  }
                />

                {compressImage && (
                  <Box>
                    <Group justify="space-between" mb="xs">
                      <MantineText size="sm" fw={500} style={accessibilityStyles}>
                        Compression Quality:
                      </MantineText>
                      <MantineText size="sm" fw={500} c={color} style={accessibilityStyles}>
                        {customQuality}%
                      </MantineText>
                    </Group>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      step="5"
                      value={customQuality}
                      onChange={(e) => {
                        const newQuality = parseInt(e.target.value);
                        setCustomQuality(newQuality);
                      }}
                      onMouseUp={() => {
                        // Reprocess image when user releases the slider
                        if (imageFile) {
                          processImage(imageFile, customQuality, compressImage);
                        }
                      }}
                      style={{
                        width: '100%',
                        height: '6px',
                        borderRadius: '3px',
                        background: `linear-gradient(to right, ${color} 0%, ${color} ${customQuality}%, #e9ecef ${customQuality}%, #e9ecef 100%)`,
                        outline: 'none',
                        cursor: 'pointer',
                      }}
                    />
                    <Group justify="space-between" mt="xs">
                      <MantineText size="xs" c="dimmed" style={accessibilityStyles}>
                        Lower quality = smaller file size
                      </MantineText>
                      <Group gap="xs">
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => {
                            setCustomQuality(50);
                            if (imageFile) {
                              processImage(imageFile, 50, compressImage);
                            }
                          }}
                        >
                          50%
                        </Button>
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => {
                            setCustomQuality(80);
                            if (imageFile) {
                              processImage(imageFile, 80, compressImage);
                            }
                          }}
                        >
                          80%
                        </Button>
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => {
                            setCustomQuality(100);
                            if (imageFile) {
                              processImage(imageFile, 100, compressImage);
                            }
                          }}
                        >
                          100%
                        </Button>
                      </Group>
                    </Group>
                  </Box>
                )}

                {(uploadingImage || processingImage) && (
                  <Paper withBorder p="md" style={{ backgroundColor: '#f8f9fa' }}>
                    <Stack align="center" gap="md">
                      <Loader size="lg" />
                      <Box style={{ textAlign: 'center' }}>
                        <MantineText size="sm" fw={500} style={accessibilityStyles}>
                          {uploadingImage ? 'Processing image...' : 'Recompressing image...'}
                        </MantineText>
                        <MantineText size="xs" c="dimmed" style={accessibilityStyles}>
                          Please wait while we optimize your image
                        </MantineText>
                      </Box>
                    </Stack>
                  </Paper>
                )}

                {imagePreview && !uploadingImage && !processingImage && (
                  <Box>
                    <MantineText size="sm" fw={500} mb="xs" style={accessibilityStyles}>
                      Preview at {insertImageWidth}% width:
                    </MantineText>
                    <Paper withBorder p="md" style={{ backgroundColor: '#f8f9fa' }}>
                      <MantineImage
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          width: `${insertImageWidth || 100}%`,
                          height: 'auto',
                          maxHeight: '400px',
                          objectFit: 'contain',
                        }}
                      />
                    </Paper>

                    {/* Size Information */}
                    <Stack gap="xs" mt="sm">
                      <Group gap="md">
                        <Box>
                          <MantineText size="xs" c="dimmed" style={accessibilityStyles}>
                            Original Size:
                          </MantineText>
                          <MantineText size="sm" fw={500} style={accessibilityStyles}>
                            {(imageSize.original / 1024).toFixed(2)} KB
                          </MantineText>
                        </Box>
                        <Box>
                          <MantineText size="xs" c="dimmed" style={accessibilityStyles}>
                            {compressImage ? 'Compressed Size:' : 'Final Size:'}
                          </MantineText>
                          <MantineText size="sm" fw={500} c={compressImage ? 'green' : 'blue'} style={accessibilityStyles}>
                            {(imageSize.compressed / 1024).toFixed(2)} KB
                          </MantineText>
                        </Box>
                        <Box>
                          <MantineText size="xs" c="dimmed" style={accessibilityStyles}>
                            Dimensions:
                          </MantineText>
                          <MantineText size="sm" fw={500} style={accessibilityStyles}>
                            {imageSize.width} × {imageSize.height}
                          </MantineText>
                        </Box>
                      </Group>

                      {compressImage && imageSize.compressed < imageSize.original && (
                        <MantineText size="xs" c="green" style={accessibilityStyles}>
                          Saved: {((1 - imageSize.compressed / imageSize.original) * 100).toFixed(1)}%
                        </MantineText>
                      )}
                      {!compressImage && (
                        <MantineText size="xs" c="blue" style={accessibilityStyles}>
                          Original image quality and dimensions preserved
                        </MantineText>
                      )}
                    </Stack>

                    {/* Insert Width Control */}
                    <Box mt="md">
                      <Group justify="space-between" mb="xs">
                        <MantineText size="sm" fw={500} style={accessibilityStyles}>
                          Display Width in Editor:
                        </MantineText>
                        <MantineText size="sm" fw={500} c="blue" style={accessibilityStyles}>
                          {insertImageWidth}% = {Math.round((imageSize.width * (insertImageWidth || 100)) / 100)}px
                        </MantineText>
                      </Group>

                      {/* Slider for percentage */}
                      <Box mb="sm">
                        <input
                          type="range"
                          min="10"
                          max="100"
                          value={insertImageWidth || 100}
                          onChange={(e) => setInsertImageWidth(parseInt(e.target.value))}
                          style={{
                            width: '100%',
                            height: '6px',
                            borderRadius: '3px',
                            background: `linear-gradient(to right, ${color} 0%, ${color} ${insertImageWidth || 100}%, #e9ecef ${insertImageWidth || 100}%, #e9ecef 100%)`,
                            outline: 'none',
                            cursor: 'pointer',
                          }}
                        />
                      </Box>

                      <Group gap="sm" align="flex-end">
                        <NumberInput
                          value={insertImageWidth}
                          onChange={(val) => setInsertImageWidth(val as number)}
                          placeholder="Width %"
                          min={1}
                          max={100}
                          suffix="%"
                          style={{ flex: 1 }}
                          description="Percentage of container width"
                        />
                        <Group gap="xs">
                          <Button size="xs" variant="light" onClick={() => setInsertImageWidth(25)}>
                            25%
                          </Button>
                          <Button size="xs" variant="light" onClick={() => setInsertImageWidth(50)}>
                            50%
                          </Button>
                          <Button size="xs" variant="light" onClick={() => setInsertImageWidth(75)}>
                            75%
                          </Button>
                          <Button size="xs" variant="light" onClick={() => setInsertImageWidth(100)}>
                            100%
                          </Button>
                        </Group>
                      </Group>
                      <MantineText size="xs" c="dimmed" mt="xs" style={accessibilityStyles}>
                        Drag the slider or enter a percentage (10-100%). The actual pixel width will depend on
                        your editor's container width.
                      </MantineText>
                    </Box>
                  </Box>
                )}

                <Group justify="flex-end" gap="sm">
                  <Button
                    variant="subtle"
                    onClick={() => {
                      setImageModalOpen(false);
                      setImageUrl('');
                      setImageFile(null);
                      setImagePreview('');
                      setInsertImageWidth('');
                      setImageSize({ original: 0, compressed: 0, width: 0, height: 0 });
                      setCompressImage(true);
                      setCustomQuality(imageQuality * 100);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="filled"
                    style={{ backgroundColor: color }}
                    onClick={addImage}
                    disabled={!imagePreview || uploadingImage}
                  >
                    Insert Image
                  </Button>
                </Group>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="url" pt="md">
              <Stack gap="md">
                <MantineTextInput
                  label="Image URL"
                  placeholder="https://example.com/image.jpg"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      addImage();
                    }
                  }}
                  autoFocus
                />
                <Group justify="flex-end" gap="sm">
                  <Button
                    variant="subtle"
                    onClick={() => {
                      setImageModalOpen(false);
                      setImageUrl('');
                      setImageFile(null);
                      setImagePreview('');
                      setInsertImageWidth('');
                      setImageSize({ original: 0, compressed: 0, width: 0, height: 0 });
                      setCompressImage(true);
                      setCustomQuality(imageQuality * 100);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="filled"
                    style={{ backgroundColor: color }}
                    onClick={addImage}
                    disabled={!imageUrl}
                  >
                    Insert Image
                  </Button>
                </Group>
              </Stack>
            </Tabs.Panel>
          </Tabs>
        </Modal>
      </Box>
    );
  }
);

HtmlEditor.displayName = 'HtmlEditor';

export default HtmlEditor;
